/**
 * The typed API. Every page talks to the server through this object; money
 * is integer cents everywhere.
 */

import type {
  Account,
  Attendance,
  BalanceSheetReport,
  BankAccount,
  Bill,
  BillPaymentInput,
  Check,
  Coupon,
  CouponInput,
  Deposit,
  DepositItem,
  EventAdmin,
  EventInput,
  EventSummary,
  JournalEntry,
  JournalEntryInput,
  Order,
  PaymentMethod,
  PaymentMethodsEnabled,
  PnlReport,
  ReceiptInput,
  Reconciliation,
  ReconciliationCandidate,
  RegisterEntryInput,
  RegisterRow,
  SiteContent,
  User,
  Vendor,
  VendorInput,
} from '@eventflow/shared';

import { get, post, put, patch, del, http, withQuery, type Query } from './http';

export interface Paged<T> {
  rows: T[];
  total: number;
  page: number;
  limit: number | 'all';
}

export interface CheckoutBody {
  event_id: string;
  ticket_option_id?: string | null;
  quantity: number;
  coupon_code?: string | null;
  attendee_names?: { first_name: string; last_name: string }[] | null;
}

export interface OrdersSummary {
  gross_cents: number;
  refunded_cents: number;
  net_cents: number;
  completed_count: number;
  refund_count: number;
  pending_count: number;
  failed_count: number;
  by_method: Partial<Record<PaymentMethod, { count: number; cents: number }>>;
  monthly: { month: string; sales_cents: number; count: number }[];
}

export interface AdminStats {
  total_events: number;
  published_events: number;
  orders: number;
  revenue_cents: number;
  gross_cents: number;
  refunded_cents: number;
  users: number;
}

export interface ReconciliationDetail {
  reconciliation: Reconciliation;
  candidates: ReconciliationCandidate[];
  cleared_balance_cents: number;
  difference_cents: number;
  resumed?: boolean;
}

export interface PrintSettings {
  bank_account: BankAccount;
  templates: { id: string; name: string; file_id: string; url: string | null; created_at: string }[];
  signature: { file_id: string; url: string | null } | null;
  font: { file_id: string; url: string | null } | null;
}

export const api = {
  auth: {
    me: () => get<{ user: User }>('/api/auth/me').then((r) => r.user),
    login: (email: string, password: string) => post<{ user: User }>('/api/auth/login', { email, password }).then((r) => r.user),
    signup: (body: { email: string; password: string; full_name: string }) =>
      post<{ user: User; verification_required: boolean }>('/api/auth/signup', body),
    logout: () => post<void>('/api/auth/logout'),
    verify: (token: string) => post<{ user: User | null }>('/api/auth/verify', { token }),
    resendVerification: () => post<{ ok: boolean; already_verified?: boolean }>('/api/auth/verify/resend'),
    forgot: (email: string) => post<{ ok: boolean }>('/api/auth/forgot', { email }),
    reset: (token: string, password: string) => post<{ user: User | null }>('/api/auth/reset', { token, password }),
    setPassword: (token: string, password: string) => post<{ user: User | null }>('/api/auth/set-password', { token, password }),
    changePassword: (current_password: string, new_password: string) =>
      post<{ ok: boolean }>('/api/auth/change-password', { current_password, new_password }),
  },
  me: {
    update: (body: Record<string, unknown>) => put<{ user: User }>('/api/me', body).then((r) => r.user),
  },
  events: {
    list: (query: { limit?: number } = {}) => get<{ events: EventSummary[] }>('/api/events', query).then((r) => r.events),
    get: (id: string) => get<{ event: EventSummary }>(`/api/events/${id}`).then((r) => r.event),
  },
  checkout: {
    card: (body: CheckoutBody & { source_id: string }) => post<{ order: Order }>('/api/checkout/card', body).then((r) => r.order),
    ach: (body: CheckoutBody & { bank_name: string; routing_number: string; account_number: string; customer_name?: string | null; customer_email?: string | null }) =>
      post<{ order: Order }>('/api/checkout/ach', body).then((r) => r.order),
    inPerson: (body: CheckoutBody & { in_person_type: 'cash' | 'check'; check_number?: string | null; customer_name?: string | null; customer_email?: string | null }) =>
      post<{ order: Order }>('/api/checkout/in-person', body).then((r) => r.order),
  },
  coupons: {
    validate: (body: { event_id: string; ticket_option_id?: string | null; quantity: number; code: string }) =>
      post<{ coupon: { code: string; discount_type: 'percentage' | 'dollar'; discount_value: number }; unit_price_cents: number; subtotal_cents: number; discount_cents: number; total_cents: number }>(
        '/api/coupons/validate',
        body,
      ),
  },
  orders: {
    mine: () => get<{ orders: Order[] }>('/api/orders/mine').then((r) => r.orders),
    get: (id: string) => get<{ order: Order }>(`/api/orders/${id}`).then((r) => r.order),
  },
  siteContent: {
    get: () => get<{ content: SiteContent | null }>('/api/site-content').then((r) => r.content),
    update: (body: Partial<SiteContent>) => put<{ content: SiteContent }>('/api/site-content', body).then((r) => r.content),
  },
  settings: {
    get: () =>
      get<{
        payment_methods: PaymentMethodsEnabled;
        square: { configured: boolean; environment: string; application_id: string | null; location_id: string | null };
        email: { configured: boolean; provider: string; domain: string | null; from: string | null };
        app: { url: string; timezone: string; build: string };
      }>('/api/settings'),
    checkout: () => get<{ payment_methods: PaymentMethodsEnabled }>('/api/settings/checkout').then((r) => r.payment_methods),
    square: () => get<{ application_id: string; location_id: string; environment: 'sandbox' | 'production' }>('/api/settings/square'),
    updatePaymentMethods: (methods: PaymentMethodsEnabled) =>
      put<{ payment_methods: PaymentMethodsEnabled }>('/api/settings/payment-methods', methods).then((r) => r.payment_methods),
  },
  uploads: {
    upload: async (file: File, kind: string) => {
      const form = new FormData();
      form.append('kind', kind);
      form.append('file', file);
      return http<{ file_id: string; url: string; name: string | null; mime: string; size: number }>('POST', '/api/uploads', { body: form });
    },
  },
  admin: {
    stats: () => get<AdminStats>('/api/admin/stats'),
    events: {
      list: () => get<{ events: EventAdmin[] }>('/api/admin/events').then((r) => r.events),
      get: (id: string) => get<{ event: EventAdmin }>(`/api/admin/events/${id}`).then((r) => r.event),
      create: (data: EventInput) => post<{ event: EventAdmin }>('/api/admin/events', data).then((r) => r.event),
      update: (id: string, data: Partial<EventInput>) => put<{ event: EventAdmin }>(`/api/admin/events/${id}`, data).then((r) => r.event),
      remove: (id: string) => del<{ ok: boolean; cancelled: boolean; message?: string }>(`/api/admin/events/${id}`),
    },
    orders: {
      list: (query: Query = {}) => get<Paged<Order>>('/api/admin/orders', query),
      summary: (query: Query = {}) => get<OrdersSummary>('/api/admin/orders/summary', query),
      get: (id: string) => get<{ order: Order }>(`/api/admin/orders/${id}`).then((r) => r.order),
      updateAttendees: (id: string, attendee_names: { first_name?: string; last_name?: string }[]) =>
        patch<{ order: Order }>(`/api/admin/orders/${id}/attendees`, { attendee_names }).then((r) => r.order),
      csvUrl: (query: Query = {}) => withQuery('/api/admin/orders/export.csv', query),
    },
    refunds: {
      create: (body: { order_id: string; amount_cents?: number; reason?: string; refund_via?: 'deposit' | 'check'; bank_account_id?: string }) =>
        post<{ order: Order | null; refund_id: string; square_refund_id: string | null }>('/api/admin/refunds', body),
    },
    coupons: {
      list: () => get<{ coupons: Coupon[] }>('/api/admin/coupons').then((r) => r.coupons),
      create: (body: CouponInput) => post<{ coupon: Coupon }>('/api/admin/coupons', body).then((r) => r.coupon),
      update: (id: string, body: Partial<CouponInput>) => put<{ coupon: Coupon }>(`/api/admin/coupons/${id}`, body).then((r) => r.coupon),
      remove: (id: string) => del<{ ok: boolean; deactivated: boolean }>(`/api/admin/coupons/${id}`),
    },
    attendance: {
      list: (event_id: string) => get<{ rows: Attendance[]; tickets_sold: number; checked_in: number }>('/api/admin/attendance', { event_id }),
      checkIn: (event_id: string, ticket_code: string) =>
        post<{ status: 'ok' | 'duplicate' | 'invalid'; message: string; attendee?: Attendance; checked_in?: number; expected?: number; tickets_sold: number }>(
          '/api/admin/attendance/check-in',
          { event_id, ticket_code },
        ),
      csvUrl: (event_id: string) => withQuery('/api/admin/attendance/export.csv', { event_id }),
    },
    users: {
      list: (query: Query = {}) => get<Paged<User>>('/api/admin/users', query),
      setRole: (id: string, role: 'user' | 'admin') => put<{ user: User }>(`/api/admin/users/${id}/role`, { role }).then((r) => r.user),
      sendSetPassword: (id: string) => post<{ ok: boolean }>(`/api/admin/users/${id}/send-set-password`),
    },
  },
  accounting: {
    accounts: {
      list: (query: { include_inactive?: boolean; type?: string } = {}) => get<{ accounts: Account[] }>('/api/accounting/accounts', query).then((r) => r.accounts),
      get: (id: string) => get<{ account: Account }>(`/api/accounting/accounts/${id}`).then((r) => r.account),
      usage: (id: string) =>
        get<{ posted_lines: number; active_events: { id: string; title: string }[]; unpaid_bills: { id: string; vendor_name: string; amount_cents: number }[]; sub_accounts: number }>(
          `/api/accounting/accounts/${id}/usage`,
        ),
      create: (body: Record<string, unknown>) => post<{ account: Account }>('/api/accounting/accounts', body).then((r) => r.account),
      update: (id: string, body: Record<string, unknown>) => put<{ account: Account }>(`/api/accounting/accounts/${id}`, body).then((r) => r.account),
      remove: (id: string) => del<{ ok: boolean }>(`/api/accounting/accounts/${id}`),
    },
    bankAccounts: {
      list: () => get<{ bank_accounts: BankAccount[] }>('/api/accounting/bank-accounts').then((r) => r.bank_accounts),
      get: (id: string) => get<{ bank_account: BankAccount }>(`/api/accounting/bank-accounts/${id}`).then((r) => r.bank_account),
      create: (body: Record<string, unknown>) => post<{ bank_account: BankAccount }>('/api/accounting/bank-accounts', body).then((r) => r.bank_account),
      update: (id: string, body: Record<string, unknown>) => put<{ bank_account: BankAccount }>(`/api/accounting/bank-accounts/${id}`, body).then((r) => r.bank_account),
      setOpeningBalance: (id: string, body: { amount_cents: number; date: string }) =>
        put<{ bank_account: BankAccount }>(`/api/accounting/bank-accounts/${id}/opening-balance`, body).then((r) => r.bank_account),
      printSettings: (id: string) => get<PrintSettings>(`/api/accounting/bank-accounts/${id}/print-settings`),
      updatePrintSettings: (id: string, body: Record<string, unknown>) => put<{ ok: boolean }>(`/api/accounting/bank-accounts/${id}/print-settings`, body),
      deleteTemplate: (templateId: string) => del<{ ok: boolean }>(`/api/accounting/bank-accounts/templates/${templateId}`),
    },
    register: {
      get: (accountId: string, query: Query = {}) =>
        get<{ account: Account; rows: RegisterRow[]; opening_cents: number; ending_cents: number }>(`/api/accounting/register/${accountId}`, query),
      addEntry: (accountId: string, body: RegisterEntryInput) => post<{ entry_id: string }>(`/api/accounting/register/${accountId}/entries`, body),
      transfer: (body: { from_account_id: string; to_account_id: string; amount_cents: number; entry_date: string; memo?: string | null }) =>
        post<{ entry_id: string }>('/api/accounting/register/transfers', body),
    },
    journal: {
      list: (query: Query = {}) => get<Paged<JournalEntry>>('/api/accounting/journal', query),
      get: (id: string) => get<{ entry: JournalEntry }>(`/api/accounting/journal/${id}`).then((r) => r.entry),
      create: (body: JournalEntryInput) => post<{ entry: JournalEntry }>('/api/accounting/journal', body).then((r) => r.entry),
      void: (id: string, reason?: string) => post<{ reversal_id: string }>(`/api/accounting/journal/${id}/void`, { reason }),
    },
    deposits: {
      list: (query: Query = {}) => get<Paged<Deposit>>('/api/accounting/deposits', query),
      get: (id: string) => get<{ deposit: Deposit; items: DepositItem[]; other_open_deposits: Deposit[] }>(`/api/accounting/deposits/${id}`),
      create: (body: { payment_method: PaymentMethod; deposit_date?: string }) => post<{ deposit: Deposit }>('/api/accounting/deposits', body).then((r) => r.deposit),
      update: (id: string, body: { merchant_fees_cents?: number; deposit_date?: string }) =>
        patch<{ deposit: Deposit }>(`/api/accounting/deposits/${id}`, body).then((r) => r.deposit),
      markDeposited: (id: string, body: { bank_account_id: string; bank_date: string; merchant_fees_cents?: number }) =>
        post<{ deposit: Deposit }>(`/api/accounting/deposits/${id}/deposit`, body).then((r) => r.deposit),
      undeposit: (id: string) => post<{ deposit: Deposit }>(`/api/accounting/deposits/${id}/undeposit`).then((r) => r.deposit),
      void: (id: string) => post<{ ok: boolean }>(`/api/accounting/deposits/${id}/void`),
      moveOrder: (id: string, order_id: string, target_deposit_id: string) =>
        post<{ ok: boolean }>(`/api/accounting/deposits/${id}/move-order`, { order_id, target_deposit_id }),
      addReceipt: (id: string, body: ReceiptInput) => post<{ receipt_id: string; items: DepositItem[] }>(`/api/accounting/deposits/${id}/receipts`, body),
      voidReceipt: (id: string, receiptId: string) => post<{ items: DepositItem[] }>(`/api/accounting/deposits/${id}/receipts/${receiptId}/void`),
      achCsvUrl: (id: string) => `/api/accounting/deposits/${id}/ach.csv`,
    },
    vendors: {
      list: (query: { include_inactive?: boolean } = {}) => get<{ vendors: Vendor[] }>('/api/accounting/vendors', query).then((r) => r.vendors),
      create: (body: VendorInput) => post<{ vendor: Vendor }>('/api/accounting/vendors', body).then((r) => r.vendor),
      update: (id: string, body: Partial<VendorInput>) => put<{ vendor: Vendor }>(`/api/accounting/vendors/${id}`, body).then((r) => r.vendor),
      remove: (id: string) => del<{ ok: boolean; deactivated: boolean; message?: string }>(`/api/accounting/vendors/${id}`),
    },
    bills: {
      list: (query: Query = {}) => get<{ bills: Bill[] }>('/api/accounting/bills', query).then((r) => r.bills),
      get: (id: string) => get<{ bill: Bill }>(`/api/accounting/bills/${id}`).then((r) => r.bill),
      create: (body: Record<string, unknown>) => post<{ bill: Bill }>('/api/accounting/bills', body).then((r) => r.bill),
      update: (id: string, body: Record<string, unknown>) => put<{ bill: Bill }>(`/api/accounting/bills/${id}`, body).then((r) => r.bill),
      void: (id: string, reason?: string) => post<{ bill: Bill }>(`/api/accounting/bills/${id}/void`, { reason }).then((r) => r.bill),
    },
    payments: {
      create: (body: BillPaymentInput) => post<{ payment_ids: string[]; checks: Check[]; printing_required: boolean }>('/api/accounting/payments', body),
      void: (id: string, reason?: string) => post<{ ok: boolean }>(`/api/accounting/payments/${id}/void`, { reason }),
    },
    checks: {
      list: (query: Query = {}) => get<{ checks: Check[] }>('/api/accounting/checks', query).then((r) => r.checks),
      print: (body: { bank_account_id: string; check_ids: string[] }) =>
        post<{ batch_id: string; first_number: number; last_number: number; pdf_url: string; checks: Check[] }>('/api/accounting/checks/print', body),
      confirmPrint: (batchId: string) => post<{ ok: boolean }>(`/api/accounting/checks/print/${batchId}/confirm`),
      spoilPrint: (batchId: string, check_ids?: string[]) => post<{ ok: boolean }>(`/api/accounting/checks/print/${batchId}/spoil`, { check_ids }),
      reprint: (id: string) => post<{ pdf_url: string }>(`/api/accounting/checks/${id}/reprint`),
      assignNumber: (id: string, number: number) => post<{ checks: Check[] }>(`/api/accounting/checks/${id}/assign-number`, { number }),
      void: (id: string, reason?: string) => post<{ checks: Check[] }>(`/api/accounting/checks/${id}/void`, { reason }),
    },
    reconciliations: {
      list: (query: { account_id?: string } = {}) => get<{ reconciliations: Reconciliation[] }>('/api/accounting/reconciliations', query).then((r) => r.reconciliations),
      start: (body: { account_id: string; statement_date: string; ending_cents: number }) => post<ReconciliationDetail>('/api/accounting/reconciliations', body),
      get: (id: string) => get<ReconciliationDetail>(`/api/accounting/reconciliations/${id}`),
      save: (id: string, body: { selected_line_ids: string[]; ending_cents?: number; statement_date?: string }) =>
        put<ReconciliationDetail>(`/api/accounting/reconciliations/${id}`, body),
      adjust: (id: string, body: { kind: 'bank_fee' | 'interest'; amount_cents: number; memo?: string }) =>
        post<ReconciliationDetail>(`/api/accounting/reconciliations/${id}/adjustments`, body),
      complete: (id: string, selected_line_ids: string[]) => post<ReconciliationDetail>(`/api/accounting/reconciliations/${id}/complete`, { selected_line_ids }),
      undo: (id: string) => post<{ ok: boolean }>(`/api/accounting/reconciliations/${id}/undo`),
      discard: (id: string) => del<{ ok: boolean }>(`/api/accounting/reconciliations/${id}`),
    },
    reports: {
      pnl: (query: { from: string; to: string; event_id?: string; detail?: boolean }) => get<PnlReport>('/api/accounting/reports/pnl', query),
      balanceSheet: (query: { as_of?: string } = {}) => get<BalanceSheetReport>('/api/accounting/reports/balance-sheet', query),
      trialBalance: (query: { as_of?: string } = {}) =>
        get<{ as_of: string | null; rows: { account_id: string; name: string; type: string; debit_cents: number; credit_cents: number }[]; debit_cents: number; credit_cents: number; balanced: boolean }>(
          '/api/accounting/reports/trial-balance',
          query,
        ),
      apAging: (query: { as_of?: string } = {}) =>
        get<{
          as_of: string;
          buckets: string[];
          vendors: { vendor_id: string | null; vendor_name: string; total_cents: number; buckets: Record<string, number>; bills: { bill_id: string; ref_no: string | null; due_date: string | null; open_cents: number; bucket: string }[] }[];
          totals: Record<string, number>;
          total_cents: number;
        }>('/api/accounting/reports/ap-aging', query),
      register: (query: { account_id: string; from?: string; to?: string; include_sub?: boolean; q?: string }) =>
        get<{ account: Account; sub_accounts: { id: string; name: string }[]; rows: RegisterRow[]; opening_cents: number; net_cents: number }>('/api/accounting/reports/register', query),
    },
    settings: {
      get: () => get<{ closed_through: string | null }>('/api/accounting/settings'),
      setClosedThrough: (closed_through: string | null) => put<{ closed_through: string | null }>('/api/accounting/settings/closed-through', { closed_through }),
    },
  },
};
